import { DomUtil, ImageOverlay } from "leaflet";
import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";

const defaultOptions = { negateX: false, negateY: false, swapXY: false };

function transformImage(img, options) {
    if (!img) return;
    let transform = img.style[DomUtil.TRANSFORM] || "";
    if (options.negateY) {
        transform += " scaleY(-1)";
    }
    if (options.negateX) {
        transform += " scaleX(-1)";
    }
    if (options.swapXY) {
        transform += " rotate(-90deg)";
    }
    img.style[DomUtil.TRANSFORM] = transform;
}

/**
 * Leaflet image overlay with CSS transforms for flip, mirror, and rotate.
 *
 * @param root0 - The component props.
 * @param root0.url - The URL of the image to overlay.
 * @param root0.bounds - The Leaflet LatLngBounds for the image.
 * @param root0.opacity - The opacity of the image overlay.
 * @param root0.zIndex - The z-index of the image layer.
 * @param root0.options - Additional transform options (flip, mirror, rotate).
 * @returns The rendered transform image overlay.
 */
const TransformImage = ({
    url,
    bounds,
    opacity = 1,
    zIndex = 1,
    options = defaultOptions,
}) => {
    const map = useMap();
    const overlayRef = useRef();

    useEffect(() => {
        if (!map) return;
        if (overlayRef.current) {
            map.removeLayer(overlayRef.current);
        }
        const TransformImageOverlay = ImageOverlay.extend({
            options: { ...defaultOptions, ...(options || {}) },
            _animateZoom: function _animateZoom(e) {
                ImageOverlay.prototype._animateZoom.call(this, e);
                transformImage(this._image, this.options);
            },
            _reset: function _reset() {
                ImageOverlay.prototype._reset.call(this);
                transformImage(this._image, this.options);
            },
        });
        const overlay = new TransformImageOverlay(url, bounds, {
            opacity,
            zIndex,
            ...(options || {}),
        });
        overlay.addTo(map);
        overlayRef.current = overlay;

        return () => {
            if (overlayRef.current) {
                map.removeLayer(overlayRef.current);
                overlayRef.current = null;
            }
        };
    }, [url, bounds, opacity, zIndex, options, map]);

    return null;
};

TransformImage.propTypes = {
    url: PropTypes.string.isRequired,
    bounds: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
    opacity: PropTypes.number,
    zIndex: PropTypes.number,
    options: PropTypes.shape({
        negateX: PropTypes.bool,
        negateY: PropTypes.bool,
        swapXY: PropTypes.bool,
    }),
};

export default TransformImage;
